 
import { IconButton, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useFormikContext } from 'formik';
import { FC, useState } from 'react';
import { DefaultTextAreaField } from 'src/components/mui-overrides/Textarea';
import { DefaultTextField } from 'src/components/mui-overrides/Textfield';
import { useAssetCustomNameFieldsIndex } from 'src/hooks/useAssetCustomNameFieldsIndex';
import EditIcon from '@mui/icons-material/Edit';
import { Iasset2LangValue } from 'src/models';
import { useTranslation } from 'react-i18next';
import { returnCurrentLangTranslationCode } from 'src/util/utils';
import { get } from 'lodash';
import { NomenclatureLabel } from '../../ui/NomenclatureLabel';
import { POST_WITH_ID } from '@brickwise/asset-validation/dist/asset/assetData';

type FlattenObjectKeys<T extends Record<string, unknown>, Key = keyof T> = Key extends string
  ? T[Key] extends Record<string, unknown>
    ? `${Key}.${FlattenObjectKeys<T[Key]>}`
    : `${Key}`
  : never;

type INomenclature = {
  rentalIncomeTitle: Iasset2LangValue[];
  rentalIncome: Iasset2LangValue[];
  managementFee: Iasset2LangValue[];
  liquidityReserve: Iasset2LangValue[];
  total: Iasset2LangValue[];
  expectedYieldDisclaimer: Iasset2LangValue[];
  documents: {
    plantList: Iasset2LangValue[];
    rentalAgreement: Iasset2LangValue[];
    expertProtocol: Iasset2LangValue[];
    landRegister: Iasset2LangValue[];
    energyCertificate: Iasset2LangValue[];
    utilityReport: Iasset2LangValue[];
  };
};

export type INomenclatureField = FlattenObjectKeys<typeof initialNomenclature>;

interface EditableFieldProps {
  // Key of the nomenclature object
  nomenclatureField: INomenclatureField;
  fallbackTranslationKey: any;
  longtext?: boolean;
  size?: 'h6' | 'body1' | 'body2' | 'overline' | undefined;
  disabled?: boolean;
}

export const initialNomenclature: INomenclature = {
  rentalIncomeTitle: [],
  rentalIncome: [],
  managementFee: [],
  liquidityReserve: [],
  total: [],
  expectedYieldDisclaimer: [],
  documents: {
    rentalAgreement: [],
    plantList: [],
    expertProtocol: [],
    landRegister: [],
    energyCertificate: [],
    utilityReport: [],
  },
};

export const EditableLabel: FC<EditableFieldProps> = ({ nomenclatureField, fallbackTranslationKey, longtext, size, disabled }) => {
  const { t } = useTranslation();
  const fallbackTranslation = t(fallbackTranslationKey);
  const languageCode = t('currentLanguageCode');
  const formik = useFormikContext<POST_WITH_ID>();
  const [editable, setEditable] = useState(false);
  const valueOfCurrentNomenClature = get(formik.values.descriptions?.nomenclature, nomenclatureField);

  const translationIndex = useAssetCustomNameFieldsIndex(valueOfCurrentNomenClature, languageCode);

  const editClicked = (field: FlattenObjectKeys<typeof initialNomenclature>, showHide: boolean, setShowHide: any) => {
    setShowHide(!showHide);
    const language = returnCurrentLangTranslationCode(t('currentLanguageCode'));
    const nomenclature = { ...formik.values.descriptions?.nomenclature };
    const currentNomenclature = (get(nomenclature, field) as Iasset2LangValue[]) ?? [];
    const hasEmptyField = currentNomenclature.some((item) => item.lang === language);
    if (!hasEmptyField) {
      formik.setFieldValue(`descriptions.nomenclature.${field}`, [...currentNomenclature, { lang: language, value: '' }]);
    }
  };

  return (
    <Grid container display="flex" alignItems="center" spacing={0}>
      <Grid sx={{ display: editable ? 'flex' : 'none', flexGrow: 1 }}>
        {longtext ? (
          <DefaultTextAreaField
            disabled={disabled}
            label={t('dashboard.assets.addedit.main.rentalincome.text.expectedYieldDisclaimerTitle')}
            fieldName={`descriptions.nomenclature.${nomenclatureField}.${translationIndex}.value`}
            formik={formik}
            placeholder={fallbackTranslation}
          />
        ) : (
          <DefaultTextField
            disabled={disabled}
            label=""
            size="small"
            fieldName={`descriptions.nomenclature.${nomenclatureField}.${translationIndex}.value`}
            placeholder={fallbackTranslation}
            formik={formik}
            variant="standard"
          />
        )}
        <IconButton sx={{ mb: 0.5, height: '42px', width: '42px' }} onClick={() => editClicked(nomenclatureField, editable, setEditable)}>
          {<EditIcon fontSize={'small'} />}
        </IconButton>
      </Grid>
      <Grid sx={{ display: editable ? 'none' : 'flex' }}>
        <Typography sx={{ display: 'flex', alignItems: 'center' }} variant={size} component={'div'}>
          <NomenclatureLabel fieldName={nomenclatureField} defaultValue={fallbackTranslation} />
        </Typography>
        <IconButton sx={{ mb: 0.5, height: '42px', width: '42px' }} onClick={() => editClicked(nomenclatureField, editable, setEditable)}>
          {<EditIcon fontSize={'small'} />}
        </IconButton>
      </Grid>
    </Grid>
  );
};
